/**
 * SCRUM-1032 — starter lists for the guided setup's Plant / Soil / Emitter
 * steps.
 *
 * Kept to the broadest categories a site is actually zoned by — turf, drip,
 * loam — so they fit a landscape, sports field, golf course or commercial site
 * without pretending to be a catalogue. Anything more specific is typed into
 * the grid.
 *
 * A chip stages a draft row rather than writing one: it is a shortcut for
 * typing the name, not a decision to create the record. The row is saved by
 * the same autosave as anything else the user typed, and can be renamed or
 * dropped before that happens.
 *
 * Plant and soil are names only — available water and intake rate are
 * site-specific and belong to the row's own fields. Emitters carry a starting
 * efficiency, because the emitter type is most of what determines it.
 */

export const PLANT_PRESETS = [
  'Turf',
  'Trees',
  'Shrubs',
  'Groundcover',
  'Flower Beds',
  'Palms',
  'Native Planting',
]

export const SOIL_PRESETS = [
  'Sand',
  'Sandy Loam',
  'Loam',
  'Silt',
  'Clay Loam',
  'Clay',
  'Rootzone Mix',
]

export const EMITTER_PRESETS = [
  'Rotor',
  'Spray',
  'Drip',
  'Micro-Spray',
  'Bubbler',
  'Subsurface Drip',
  'Impact',
]

/**
 * Starting application efficiency per emitter type, as a percentage.
 *
 * These are the mid-range figures the industry designs to — the Irrigation
 * Association's landscape audit values and ASABE's sprinkler ranges — not a
 * measurement of this site. The spread is the point: a spray head loses a third
 * of its water to wind and overspray, subsurface drip loses almost none. An
 * audited site should overwrite these with its own numbers, which is why the
 * cell stays editable.
 */
export const EMITTER_EFFICIENCY_PRESETS: Record<string, number> = {
  // Overlapping throw, coarse droplets: less drift than a spray, more than drip.
  Rotor: 70,
  // Fine droplets over a short throw — the most exposed to wind and overspray.
  Spray: 65,
  Impact: 70,
  'Micro-Spray': 80,
  // Applied at the base, no throw to lose.
  Bubbler: 85,
  Drip: 90,
  // Below grade: no evaporation, no wind, no runoff.
  'Subsurface Drip': 95,
}
